// App.js — patched to render answers in a 2×2 grid (no other logic changed)

import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import QRCode from "qrcode";
import { io } from "socket.io-client";
import { QUESTION_STAGE_TYPES } from "./shared/questionStages";
import { DISPLAY_STAGES } from "./shared/displayStages";
import { sanitizeLeaderboardEntries } from "./shared/leaderboardSanitizer";
import {
  createStageStallMonitor,
  isStageOlderThan,
  resolveStageTimelineStamp,
} from "./shared/stageSync";
import DisplayAnimationLab from "./animations/DisplayAnimationLab";
import GameStageTransition from "./animations/GameStageTransition";
import {
  areDisplayAnimationsEnabled,
  isDisplayAnimationLabEnabled,
} from "./animations/displayAnimationConfig";
import { getLiveDisplayTransitionPreset } from "./animations/displayTransitions";
import "./DisplayBackground.css";
import useQuestionTimerAudio from "./audio/useQuestionTimerAudio";


const QUESTION_TITLE_FIT = Object.freeze({
  minFontSize: 18,
  maxFontSize: 52,
  lineHeight: 1.3,
});
const FUN_FACT_TITLE_FIT = Object.freeze({
  minFontSize: 16,
  maxFontSize: 40,
  lineHeight: 1.45,
});
const ANSWER_OPTION_FIT = Object.freeze({
  minFontSize: 15,
  maxFontSize: 34,
  lineHeight: 1.3,
});

export const SERVER_TIME_SYNC_SAMPLE_COUNT = 5;
const GAME_START_INTRO_STAGE = "game-start-intro";

export const buildServerTimeRequestPayload = () => ({ clientSentAt: Date.now() });

export const resolveServerTimeOffsetSample = ({ now, clientSentAt } = {}, receivedAt = Date.now()) => {
  if (typeof now !== "number" || typeof clientSentAt !== "number") return null;
  const roundTripMs = Math.max(0, receivedAt - clientSentAt);
  const estimatedServerNowAtReceipt = now + (roundTripMs / 2);
  return {
    roundTripMs,
    estimatedServerNowAtReceipt,
    offset: estimatedServerNowAtReceipt - receivedAt,
  };
};

export const getMeasurableText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(getMeasurableText).join("");
  }
  return "";
};

/*
 * A word the browser has no place to wrap inside: Latin letters (accented
 * ones included), digits and apostrophes, with opening brackets or quotes
 * before and closing ones or sentence punctuation after ("Planet?",
 * "(1999)"). Anything else may wrap inside - after a hyphen, dash or slash,
 * or between letters of other scripts - so it is held together. When unsure,
 * hold it: that costs nothing on screen. Plain ranges rather than Unicode
 * property escapes, which Babel expands into kilobytes of tables.
 */
const UNBREAKABLE_WORD =
  /^[("“‘]*[A-Za-z0-9À-ÖØ-öø-ɏ̀-ͯ'’]+[)"”’.,:;!?]*$/;

/*
 * Lays fitted text out as whole words. `hyphens: none` stops the browser
 * adding hyphens, but it still wraps after a hyphen, dash or slash inside a
 * word ("Winston-Salem", "AC/DC"), and that wrap never overflows, so the fit
 * would not shrink it. Such a word is held on one line instead, so only the
 * spaces between words wrap. Text with no such word stays one plain string.
 */
export const renderWholeWords = (value) => {
  if (typeof value !== "string" && typeof value !== "number") return value;
  const parts = String(value).split(/(\s+)/).filter(Boolean);
  if (parts.every((part) => /^\s+$/.test(part) || UNBREAKABLE_WORD.test(part))) {
    return value;
  }
  return parts.map((part, index) =>
    /^\s+$/.test(part) || UNBREAKABLE_WORD.test(part) ? (
      part
    ) : (
      <span key={index} className="fit-text__word">
        {part}
      </span>
    ),
  );
};

/*
 * Lets several AutoFitText boxes share one size. Each member still fits on
 * its own and reports that size, and every member shows the smallest, so
 * the four answer tiles read as one set.
 *
 * The group writes the size straight onto each member's text, rather than
 * through React state: fitting overwrites a member's size while it measures,
 * and the shared size is only known once the last member has reported.
 */
export const createAutoFitGroup = () => {
  const members = new Map();
  const applySmallest = () => {
    let smallest = null;
    members.forEach(({ size }) => {
      if (size !== null && (smallest === null || size < smallest)) {
        smallest = size;
      }
    });
    if (smallest === null) return;
    members.forEach(({ element }) => {
      element.style.fontSize = `${smallest}px`;
    });
  };
  return {
    report(member, element, size) {
      members.set(member, { element, size });
      applySmallest();
    },
    leave(member) {
      members.delete(member);
      applySmallest();
    },
  };
};

export function useAutoFitGroup() {
  const [group] = useState(createAutoFitGroup);
  return group;
}

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

export function AutoFitText({
  as: Component = "span",
  children,
  className,
  innerClassName = "",
  minFontSize,
  maxFontSize,
  lineHeight,
  trailing = null,
  fitGroup = null,
  ...props
}) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const rafRef = useRef(null);
  const groupMemberRef = useRef({});
  const [fontSize, setFontSize] = useState(maxFontSize);
  const text = getMeasurableText(children);
  // Trailing content (the correct-answer check) takes a grid column from the
  // text without resizing the container, so its arrival must refit on its own.
  const hasTrailing = trailing !== null && trailing !== undefined && trailing !== false;

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    const textElement = textRef.current;
    if (!container || !textElement) return undefined;

    const cancelPendingFit = () => {
      if (rafRef.current !== null) {
        if (typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(rafRef.current);
        } else {
          clearTimeout(rafRef.current);
        }
        rafRef.current = null;
      }
    };

    /*
     * Words never split, so a word wider than its line overflows sideways and
     * shows up in scrollWidth. Measure against the narrower of the container
     * and the text's own box: an answer's text sits in the middle column of
     * its card, and a word can overflow that column while still fitting the
     * card.
     */
    const availableWidth = () => {
      const ownWidth = textElement.clientWidth;
      return ownWidth > 0
        ? Math.min(ownWidth, container.clientWidth)
        : container.clientWidth;
    };

    const overflowsWidth = () => textElement.scrollWidth > availableWidth() + 1;

    const fitsAt = (size) => {
      textElement.style.fontSize = `${size}px`;
      return (
        !overflowsWidth() &&
        textElement.scrollHeight <= container.clientHeight + 1
      );
    };

    // In a group, the group then puts the shared size on every member,
    // including this one, whose size the measuring has just overwritten.
    // A box that could not be measured does not lower the shared size.
    const commitFit = (size, measured) => {
      setFontSize(size);
      if (fitGroup) {
        fitGroup.report(groupMemberRef.current, textElement, measured ? size : null);
      }
    };

    const runFit = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;

      if (width <= 0 || height <= 0 || !text) {
        commitFit(maxFontSize, false);
        return;
      }

      textElement.style.lineHeight = String(lineHeight);

      if (fitsAt(maxFontSize)) {
        commitFit(maxFontSize, true);
        return;
      }

      let low = minFontSize;
      let high = maxFontSize;
      let best = minFontSize;

      for (let i = 0; i < 9; i += 1) {
        const mid = (low + high) / 2;
        if (fitsAt(mid)) {
          best = mid;
          low = mid;
        } else {
          high = mid;
        }
      }

      let fittedSize = Math.max(minFontSize, Math.min(maxFontSize, best));
      textElement.style.fontSize = `${fittedSize}px`;
      /*
       * Words are never broken, with or without a hyphen. A word longer than
       * a whole line even at minFontSize would otherwise be clipped, so the
       * width rule outranks the minimum: keep shrinking until the word fits.
       * A word's width scales with the font size, so scaling by the overflow
       * ratio lands on it in a step or two.
       */
      for (let i = 0; i < 4 && overflowsWidth(); i += 1) {
        const ratio = availableWidth() / textElement.scrollWidth;
        fittedSize = Math.max(1, Math.floor(fittedSize * ratio * 100) / 100);
        textElement.style.fontSize = `${fittedSize}px`;
      }
      commitFit(fittedSize, true);
    };

    const scheduleFit = () => {
      cancelPendingFit();
      const scheduleFrame =
        typeof requestAnimationFrame === "function"
          ? requestAnimationFrame
          : (callback) => setTimeout(callback, 0);
      rafRef.current = scheduleFrame(() => {
        rafRef.current = null;
        runFit();
      });
    };

    runFit();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleFit);
      resizeObserver.observe(container);
    } else {
      window.addEventListener("resize", scheduleFit);
    }

    /*
     * The display faces are self-hosted with font-display: swap, so the
     * measurement above can land against the fallback face. Swapping in the
     * real face changes glyph metrics but not the container box - these sit
     * in fixed grid cells - so the ResizeObserver never fires and the fitted
     * size stays stale, clipping long questions and answers on a cold load.
     */
    let isUnmounted = false;
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    const handleFontsLoaded = () => {
      if (!isUnmounted) {
        scheduleFit();
      }
    };

    if (fonts && typeof fonts.addEventListener === "function") {
      fonts.addEventListener("loadingdone", handleFontsLoaded);
    }
    if (
      fonts &&
      fonts.status !== "loaded" &&
      fonts.ready &&
      typeof fonts.ready.then === "function"
    ) {
      fonts.ready.then(handleFontsLoaded).catch(() => {});
    }

    return () => {
      isUnmounted = true;
      cancelPendingFit();
      if (fonts && typeof fonts.removeEventListener === "function") {
        fonts.removeEventListener("loadingdone", handleFontsLoaded);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", scheduleFit);
      }
      if (fitGroup) {
        fitGroup.leave(groupMemberRef.current);
      }
    };
  }, [fitGroup, hasTrailing, lineHeight, maxFontSize, minFontSize, text]);

  return (
    <Component ref={containerRef} className={className} {...props}>
      <span
        ref={textRef}
        className={`fit-text__inner${innerClassName ? ` ${innerClassName}` : ""}`}
        // A group sets its members' size itself; see createAutoFitGroup.
        style={fitGroup ? { lineHeight } : { fontSize: `${fontSize}px`, lineHeight }}
      >
        {renderWholeWords(children)}
      </span>
      {trailing}
    </Component>
  );
}

const isAnimationLabRequested = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return new URLSearchParams(window.location.search).get("animationLab") === "1";
};

export const shouldRenderAnimationLab = () =>
  isDisplayAnimationLabEnabled() && isAnimationLabRequested();

const socket = shouldRenderAnimationLab()
  ? null
  : io({
      query: {
        role: "display",
      },
    });
const DEFAULT_PARLOR_CODE = "default";
// The controller's guest-first entry screen: a nickname is enough to play.
// /controller/login would open the account-login form instead.
const DEFAULT_CONTROLLER_QR_TARGET_URL =
  "https://brain-battle.onrender.com/controller";
const controllerQrTargetOverride =
  typeof process !== "undefined" &&
  process.env &&
  typeof process.env.REACT_APP_CONTROLLER_QR_TARGET_URL === "string"
    ? process.env.REACT_APP_CONTROLLER_QR_TARGET_URL.trim()
    : "";
const CONTROLLER_QR_TARGET_URL =
  controllerQrTargetOverride || DEFAULT_CONTROLLER_QR_TARGET_URL;

const buildQrCodeImageUrl = async (targetUrl) => {
  if (!targetUrl) return "";
  try {
    return await QRCode.toDataURL(targetUrl, {
      width: 352,
      margin: 1,
    });
  } catch {
    return "";
  }
};

const AD_BREAK_COPY =
  "You are playing Brain Battle Live! New games starting continuously, 24 hours a day. Challenge your friends or take on the world in real-time mind stretching trivia, and use that big brain of yours to think your way up the leaderboards! Scan the QR code to share with your friends.";
const buildSmsShareBody = (targetUrl) => {
  const baseMessage =
    "I'm playing Brain Battle Live! New games start continuously, 24 hours a day. Challenge me or challenge the world in real-time mind stretching trivia.";
  return targetUrl ? `${baseMessage} Join here: ${targetUrl}` : baseMessage;
};
const buildSmsShareUrl = (targetUrl) => {
  const body = buildSmsShareBody(targetUrl);
  return body ? `sms:?&body=${encodeURIComponent(body)}` : "";
};
const AD_BREAK_SHARE_SMS_URI = buildSmsShareUrl(CONTROLLER_QR_TARGET_URL);
const HAS_CONTROLLER_QR = Boolean(CONTROLLER_QR_TARGET_URL);
const HAS_AD_BREAK_SHARE_QR = Boolean(AD_BREAK_SHARE_SMS_URI);

const TEXT_KEYS = Object.freeze([
  "text",
  "fact",
  "fun_fact",
  "funFact",
  "fun_fact_text",
  "funFactText",
  "value",
  "content",
  "body",
  "description",
  "message",
]);

const TEXT_KEYS_SET = new Set(TEXT_KEYS);

const FAST_PATH_KEYS = Object.freeze([
  "funFacts",
  "fun_facts",
  "fun_fact_variants",
  "alternates",
  "alternatesList",
  "funFactAlternates",
  "fun_fact_alternates",
  "funFact",
  "fun_fact",
  "funFactText",
  "fun_fact_text",
]);

/**
 * ⚡ BOLT OPTIMIZATION: pickRandomFunFact.
 * Optimized with fast-paths for standard keys and deferred object allocations.
 * Uses optimized loops and avoids redundant Set/WeakSet creation for simple payloads.
 * Estimated Impact: ~10-20x speedup for standard question payloads; reduced memory churn.
 */
export const pickRandomFunFact = (payload) => {
  if (!payload) return null;

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    return trimmed || null;
  }

  if (typeof payload !== "object") return null;

  const candidates = [];
  let seen = null;
  let visited = null;

  const addCandidate = (value) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (seen !== null && seen.has(trimmed)) return;

    if (seen === null) seen = new Set();
    seen.add(trimmed);
    candidates.push(trimmed);
  };

  const collect = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      for (let i = 0, len = value.length; i < len; i++) {
        collect(value[i]);
      }
      return;
    }
    if (typeof value === "object") {
      if (visited === null) visited = new WeakSet();
      if (visited.has(value)) return;
      visited.add(value);

      for (let i = 0, len = TEXT_KEYS.length; i < len; i++) {
        const key = TEXT_KEYS[i];
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          collect(value[key]);
        }
      }

      const keys = Object.keys(value);
      for (let i = 0, len = keys.length; i < len; i++) {
        const key = keys[i];
        if (TEXT_KEYS_SET.has(key)) continue;
        const nested = value[key];
        if (nested && typeof nested === "object") {
          collect(nested);
        }
      }

      return;
    }
    addCandidate(value);
  };

  for (let i = 0, len = FAST_PATH_KEYS.length; i < len; i++) {
    const key = FAST_PATH_KEYS[i];
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      collect(payload[key]);
      if (candidates.length > 0) break;
    }
  }

  // Note: We do not fall back to collecting the payload object directly if no
  // candidates were found in standard keys, matching the original logic's
  // restrictive search behavior for object inputs.

  if (candidates.length === 0) return null;
  if (process.env.NODE_ENV === "test") {
    return candidates[0];
  }
  const randomIndex = Math.floor(Math.random() * candidates.length);
  return candidates[randomIndex];
};

const QUESTION_PHASES = QUESTION_STAGE_TYPES;

const DEFAULT_AD_BREAK_TIMER_LABEL = "Next game starts in";
export const ROUND_INTRO_DURATION_MS = 3000;

const normalizePositiveRoundNumber = (value) => {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

export const resolveNextRoundNumber = ({
  metadata,
  nextRoundNumber,
  roundNumber,
} = {}) => {
  const explicitNextRound = normalizePositiveRoundNumber(nextRoundNumber);
  if (explicitNextRound) {
    return explicitNextRound;
  }

  const metadataNextRound = normalizePositiveRoundNumber(
    metadata?.nextRoundNumber,
  );
  if (metadataNextRound) {
    return metadataNextRound;
  }

  const completedRound =
    normalizePositiveRoundNumber(roundNumber) ??
    normalizePositiveRoundNumber(metadata?.roundNumber) ??
    normalizePositiveRoundNumber(metadata?.currentRoundNumber);

  if (completedRound) {
    return completedRound + 1;
  }

  return null;
};

const resolveAdBreakTimerLabel = ({
  adSlot,
  metadata,
  nextRoundNumber,
  roundNumber,
} = {}) => {
  if (adSlot !== "between-rounds") {
    return DEFAULT_AD_BREAK_TIMER_LABEL;
  }

  const resolvedNextRoundNumber = resolveNextRoundNumber({
    metadata,
    nextRoundNumber,
    roundNumber,
  });

  if (resolvedNextRoundNumber) {
    return `Round ${resolvedNextRoundNumber} starts in`;
  }

  return "Next round starts in";
};

const LEADERBOARD_SCREEN_COPY = {
  [DISPLAY_STAGES.SHOW_QUESTION_RESULTS]: {
    heading: "Parlor Scores",
    fallbackSubtitle: "This Question",
    leaderboardView: "question",
    empty: "No question results yet",
  },
  [DISPLAY_STAGES.SHOW_QUESTION_GLOBAL_TOP_100]: {
    heading: "Global Scores",
    fallbackSubtitle: "This Question",
    leaderboardView: "global",
    empty: "No global players yet",
  },
  [DISPLAY_STAGES.SHOW_ROUND_PARLOR_STANDINGS]: {
    heading: "Parlor Standings",
    fallbackSubtitle: "Current Round",
    leaderboardView: "parlor",
    empty: "No players in this parlor",
  },
  [DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100]: {
    heading: "Global Standings",
    fallbackSubtitle: "Current Game",
    leaderboardView: "global",
    empty: "No global players yet",
  },
  [DISPLAY_STAGES.GAME_OVER]: {
    heading: "Global Standings",
    fallbackSubtitle: "Final Results",
    leaderboardView: "global",
    empty: "No final results yet",
  },
};

const DISPLAY_WORDMARK = "Brain Battle Live!";

// Every leaderboard stage shows a top-five podium; ranks 6-100 flow into the
// dense grid below it. Podium and row accents come from DisplayBackground.css.
const LEADERBOARD_PODIUM_SIZE = 5;
const LEADERBOARD_MAX_GRID_ENTRIES = 95;
const FINAL_RESULTS_SUBTITLE = "Final Results";
// Cadence for rotating between Parlor and Global post-game final standings.
const POST_GAME_VIEW_ROTATION_MS = 8000;
// The wrap-up shows both final standings boards, so a wrap-up (or the part
// of one left when the display joins) too short for two full turns splits
// its time between them instead. There is no minimum turn: any floor longer
// than half the time left would end the wrap-up before Global's turn.
const POST_GAME_VIEW_COUNT = 2;

export const resolvePostGameViewRotationMs = (payload) => {
  const budgetMs =
    typeof payload?.remainingMs === "number" && Number.isFinite(payload.remainingMs)
      ? payload.remainingMs
      : typeof payload?.duration === "number" && Number.isFinite(payload.duration)
        ? payload.duration
        : null;
  if (budgetMs === null || budgetMs <= 0) return POST_GAME_VIEW_ROTATION_MS;
  return Math.max(
    1,
    Math.min(
      POST_GAME_VIEW_ROTATION_MS,
      Math.floor(budgetMs / POST_GAME_VIEW_COUNT),
    ),
  );
};

const ANSWER_LABELS = Object.freeze(["A", "B", "C", "D"]);

const buildQuestionLeaderboard = (result) => {
  if (!result) return [];
  const summaries = Array.isArray(result.playerSummaries)
    ? result.playerSummaries
    : [];

  return sanitizeLeaderboardEntries(summaries)
    .map((player) => ({
      name: player.name,
      score:
        typeof player.pointsAwarded === "number" ? player.pointsAwarded : 0,
    }))
    .filter(
      (player) =>
        typeof player.name === "string" && player.name.trim().length > 0,
    )
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.name.localeCompare(b.name);
    });
};

const resolveStageDurationMs = ({ remainingMs, durationMs, duration }) => {
  if (typeof remainingMs === "number" && !Number.isNaN(remainingMs)) {
    return remainingMs;
  }
  if (typeof durationMs === "number" && !Number.isNaN(durationMs)) {
    return durationMs;
  }
  if (typeof duration === "number" && !Number.isNaN(duration)) {
    return duration;
  }
  return null;
};

const normalizeMsTimestamp = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  return value < 1000000000000 ? value * 1000 : value;
};

const normalizeMsDuration = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  return value <= 600 ? value * 1000 : value;
};

const toLocalTime = (serverTimestamp, offset) => serverTimestamp - offset;

// Local start and end of a stage, for a sound that starts with it. A replayed
// snapshot carries remainingMs; a live stage carries its server start.
export const resolveStageAudio = (payload, offset, now = Date.now()) => {
  const metadata = payload?.metadata || {};
  const stageStart = normalizeMsTimestamp(metadata.timelineTimestamp);
  const duration = normalizeMsDuration(payload?.duration);
  let startTime = now;
  if (typeof payload?.remainingMs === "number" && payload.remainingMs >= 0 && duration !== null) {
    startTime = now + payload.remainingMs - duration;
  } else if (stageStart !== null) {
    startTime = toLocalTime(stageStart, offset);
  }
  const endTime = duration !== null ? startTime + duration : null;
  // Stable across duplicate events and snapshots, distinct across games.
  const key = `${metadata.questionNumber ?? ""}:${stageStart ?? Math.round(startTime / 1000)}`;
  return { startTime, endTime, key };
};

// The final standings of a game. The post-game wrap-up is a timed stage; the
// game-over event (a non-cycling game ends with the wrap-up and then game-over,
// and it is replayed to a display joining after one) has no stage timing, so it
// starts now. Both carry the game's id, so both are keyed to the same game.
export const resolveGameOverAudio = (payload, offset, now = Date.now()) => {
  const timed = typeof payload?.metadata?.timelineTimestamp === "number" ||
    typeof payload?.remainingMs === "number";
  const resolved = timed
    ? resolveStageAudio(payload, offset, now)
    : { startTime: now, endTime: null, key: `${Math.round(now / 1000)}` };
  return payload?.gameId ? { ...resolved, key: `game:${payload.gameId}` } : resolved;
};

// Keeps the first timing for a game, so a later event for it cannot restart the sound.
const keepGameOverAudio = (next) => (current) =>
  current && next && current.key === next.key ? current : next;

// The first question intro of a round (game-start intro for round 1), or null
// for any other intro. The intro's end also ends the sound, so an intro shorter
// than the sound leaves the reading period silent.
export const resolveRoundIntroAudio = (payload, offset, now = Date.now()) => {
  const metadata = payload?.metadata || {};
  if (metadata.questionInRound !== 1 && metadata.questionNumber !== 1) return null;
  return resolveStageAudio(payload, offset, now);
};

const calculateQuestionCountdownProgress = (countdown, now = Date.now()) => {
  if (!countdown?.visible) return 0;
  const { startTime, endTime, durationMs } = countdown;
  if (typeof startTime !== "number" || typeof endTime !== "number") {
    return 100;
  }
  if (endTime <= startTime) return 0;
  if (now >= endTime) return 0;
  if (now <= startTime) return 100;

  const totalDuration =
    typeof durationMs === "number" && durationMs > 0
      ? durationMs
      : endTime - startTime;
  if (totalDuration <= 0) return 0;

  const remainingMs = Math.max(0, endTime - now);
  return Math.max(0, Math.min(100, (remainingMs / totalDuration) * 100));
};

const hiddenQuestionCountdownBar = Object.freeze({
  visible: false,
  startTime: null,
  endTime: null,
  durationMs: null,
});

const getElapsedQuestionCountdownBar = () => {
  const now = Date.now();
  return {
    visible: true,
    startTime: now,
    endTime: now,
    durationMs: 0,
  };
};

const buildClassName = (...classNames) => classNames.filter(Boolean).join(" ");

// True when the card on screen is for a different question than the stage
// names. Unknown numbers on either side never count as a mismatch.
export const isQuestionForDifferentStage = (questionPayload, metadata) => {
  const shown = Number(questionPayload?.questionNumber);
  const staged = Number(metadata?.questionNumber);
  return (
    Number.isInteger(shown) &&
    shown > 0 &&
    Number.isInteger(staged) &&
    staged > 0 &&
    shown !== staged
  );
};

// The scored question a fun-fact payload carries (`result`), as a question
// card can show it. Lets a display that missed the reveal still show the
// options under the fun fact.
export const resolveQuestionFromResult = (result) => {
  if (!result || !Array.isArray(result.options) || result.options.length === 0) {
    return null;
  }
  return {
    questionText: result.questionText ?? result.question ?? null,
    options: result.options,
    roundNumber: result.roundNumber ?? null,
    questionNumber: result.questionNumber ?? null,
    questionInRound: result.questionInRound ?? null,
  };
};

const QuestionCountdownBar = React.memo(function QuestionCountdownBar({ countdown }) {
  const [frameNow, setFrameNow] = useState(() => Date.now());

  useEffect(() => {
    if (!countdown?.visible || countdown.endTime <= Date.now()) return undefined;

    let animationFrameId = null;
    const updateQuestionCountdownFrame = () => {
      const nextFrameNow = Date.now();
      setFrameNow(nextFrameNow);

      if (countdown.endTime > nextFrameNow) {
        animationFrameId = window.requestAnimationFrame(updateQuestionCountdownFrame);
      }
    };

    updateQuestionCountdownFrame();

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [countdown]);

  if (!countdown?.visible) return null;

  const countdownNow =
    countdown.durationMs === 0
      ? countdown.endTime
      : countdown.endTime <= Date.now()
        ? Date.now()
        : frameNow;
  const progress = calculateQuestionCountdownProgress(countdown, countdownNow);
  const progressBarProps =
    countdown.durationMs !== 0
      ? {
          role: "progressbar",
          "aria-label": "Question score countdown",
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          "aria-valuenow": Math.round(progress),
        }
      : {
          "aria-hidden": true,
        };

  return (
    <div
      className="question-countdown-bar"
      data-testid="question-countdown-bar"
      style={{
        "--question-countdown-progress": `${progress}%`,
      }}
      {...progressBarProps}
    >
      <span className="question-countdown-bar__fill" />
    </div>
  );
});

const formatQuestionLabel = (metadata) => {
  if (!metadata) return "";
  const { roundNumber, sequenceType } = metadata;
  const overallQuestionNumber = resolveOverallQuestionNumber(metadata);

  if (sequenceType === "question") {
    return overallQuestionNumber ? `Question ${overallQuestionNumber}` : "";
  }
  const parts = [];
  if (roundNumber) parts.push(`Round ${roundNumber}`);
  if (overallQuestionNumber) {
    parts.push(`Question ${overallQuestionNumber}`);
  }
  return parts.join(" • ");
};

const resolveOverallQuestionNumber = (metadata) => {
  if (!metadata) return null;
  if (Number.isInteger(metadata.questionNumber) && metadata.questionNumber > 0) {
    return metadata.questionNumber;
  }
  if (Number.isInteger(metadata.questionIndex) && metadata.questionIndex >= 0) {
    return metadata.questionIndex + 1;
  }
  return null;
};

const buildStatus = (prefix, metadata) => {
  const label = formatQuestionLabel(metadata);
  return label && prefix ? prefix + " • " + label : (label || prefix);
};

const buildQuestionStatus = (metadata, fallback) =>
  formatQuestionLabel(
    metadata
      ? {
          ...metadata,
          sequenceType: "question",
        }
      : metadata,
  ) || fallback;

const AUTH_ERROR_TYPES = new Set([
  "unauthorized-admin-key",
]);

const normalizeSocketErrorType = (error) => {
  if (!error) return "unknown";
  if (typeof error === "string") return error.trim().toLowerCase();
  if (typeof error.message === "string") {
    return error.message.trim().toLowerCase();
  }
  return "unknown";
};

const isUnauthorizedKeyError = (errorType) =>
  AUTH_ERROR_TYPES.has(errorType) || errorType.includes("unauthorized");

class DisplayErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (typeof this.props.onError === "function") {
      this.props.onError({
        error,
        componentStack: info?.componentStack || "",
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <section
          className="card connection-error-card"
          data-testid="display-render-fallback"
        >
          <h2>⚠️ Display Render Issue</h2>
          <p>
            The display encountered an unexpected render error and cannot show
            the current game view.
          </p>
          <p className="connection-error-card__hint">
            Please refresh the display browser to recover.
          </p>
        </section>
      );
    }

    return this.props.children;
  }
}

const LIVE_DISPLAY_STAGE_TRANSITION_STYLE = Object.freeze({
  width: "100%",
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
});

const leaderboardEntryKeyByObject = new WeakMap();
let nextLeaderboardEntryFallbackKey = 1;

function getLeaderboardEntryKey(displayStage, player) {
  if (player?.id) {
    return `${displayStage}-${player.id}`;
  }

  if (player && typeof player === "object") {
    if (!leaderboardEntryKeyByObject.has(player)) {
      leaderboardEntryKeyByObject.set(
        player,
        `entry-${nextLeaderboardEntryFallbackKey}`,
      );
      nextLeaderboardEntryFallbackKey += 1;
    }

    return `${displayStage}-${leaderboardEntryKeyByObject.get(player)}`;
  }

  return `${displayStage}-entry-unknown`;
}

function DisplayWordmark({ className = "", animateText = false }) {
  return (
    <div
      className={buildClassName(
        "display-wordmark",
        className,
        animateText ? "display-stage-text-fade" : "",
      )}
    >
      {DISPLAY_WORDMARK}
    </div>
  );
}

// One layout for every leaderboard stage: wordmark / heading / subtitle across
// the top, a top-five podium, then ranks 6-100 in a dense column-flow grid.
export function LeaderboardBoard({
  heading,
  subtitle,
  entries,
  entryKeyScope,
  testId = "leaderboard-card",
  animateText = false,
}) {
  const safeEntries = (Array.isArray(entries) ? entries : []).filter(Boolean);
  const podium = safeEntries.slice(0, LEADERBOARD_PODIUM_SIZE);
  const grid = safeEntries.slice(
    LEADERBOARD_PODIUM_SIZE,
    LEADERBOARD_PODIUM_SIZE + LEADERBOARD_MAX_GRID_ENTRIES,
  );
  const fadeClass = animateText ? "display-stage-text-fade" : "";

  return (
    <section
      className="card display-screen-card leaderboard-card"
      data-testid={testId}
    >
      <div className="leaderboard-header">
        <DisplayWordmark
          className="leaderboard-header__wordmark"
          animateText={animateText}
        />
        <h2 className={fadeClass}>{heading}</h2>
        {subtitle && (
          <p className={buildClassName("leaderboard-subtitle", fadeClass)}>
            {subtitle}
          </p>
        )}
      </div>
      {podium.length === 0 ? (
        <p className={buildClassName("leaderboard-empty", fadeClass)}>
          No leaderboard data yet.
        </p>
      ) : (
        <div className="leaderboard-container">
          <ol className="leaderboard-podium">
            {podium.map((player, index) => (
              <li
                key={getLeaderboardEntryKey(`${entryKeyScope}-podium`, player)}
                className="leaderboard-pod"
              >
                <span className={buildClassName("leaderboard-pod__rank", fadeClass)}>
                  {index + 1}
                </span>
                <span className={buildClassName("leaderboard-pod__name", fadeClass)}>
                  {player.name || ""}
                </span>
                <span className={buildClassName("leaderboard-pod__score", fadeClass)}>
                  {player.score ?? 0} pts
                </span>
              </li>
            ))}
          </ol>
          {grid.length > 0 && (
            <ol className="leaderboard-list" start={LEADERBOARD_PODIUM_SIZE + 1}>
              {grid.map((player, index) => (
                <li
                  key={getLeaderboardEntryKey(`${entryKeyScope}-grid`, player)}
                  className="leaderboard-item"
                >
                  <span className={buildClassName("leaderboard-rank", fadeClass)}>
                    {LEADERBOARD_PODIUM_SIZE + index + 1}
                  </span>
                  <span className={buildClassName("leaderboard-name", fadeClass)}>
                    {player.name || ""}
                  </span>
                  <span className={buildClassName("leaderboard-score", fadeClass)}>
                    {player.score ?? 0}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

export function LiveDisplayStageTransition({ children, uiState, displayStage }) {
  const preset = getLiveDisplayTransitionPreset(displayStage);

  if (
    uiState !== "game" ||
    !areDisplayAnimationsEnabled() ||
    !preset
  ) {
    // Unknown or non-game stages render plainly instead of using a catch-all animation.
    return <>{children}</>;
  }

  return (
    <GameStageTransition
      uiState={uiState}
      displayStage={displayStage}
      preset={preset}
      initial
      style={LIVE_DISPLAY_STAGE_TRANSITION_STYLE}
    >
      {children}
    </GameStageTransition>
  );
}

function LiveDisplayApp() {
  // The four answer tiles share one text size: the smallest any one needs.
  const answerFitGroup = useAutoFitGroup();
  const [uiState, setUiState] = useState("welcome");
  const [parlorStatusMessage, setParlorStatusMessage] = useState("");
  const [question, setQuestion] = useState(null);
  const [status, setStatus] = useState("Waiting for game...");
  const [questionCountdownBar, setQuestionCountdownBar] = useState(
    hiddenQuestionCountdownBar,
  );
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboards, setLeaderboards] = useState({ parlor: [], global: [] });
  const [postGameViewIndex, setPostGameViewIndex] = useState(0);
  const [postGameViewRotationMs, setPostGameViewRotationMs] = useState(
    POST_GAME_VIEW_ROTATION_MS,
  );
  const [displayStage, setDisplayStage] = useState(DISPLAY_STAGES.WELCOME);
  const [leaderboardTitle, setLeaderboardTitle] = useState("");
  const [correctIndex, setCorrectIndex] = useState(null);
  const [isAdBreak, setIsAdBreak] = useState(false);
  const [adTimeLeft, setAdTimeLeft] = useState(0);
  const [adBreakTimerLabel, setAdBreakTimerLabel] = useState(
    DEFAULT_AD_BREAK_TIMER_LABEL,
  );
  const [isRoundIntro, setIsRoundIntro] = useState(false);
  const [upcomingRoundNumber, setUpcomingRoundNumber] = useState(null);
  const [controllerQrImageUrl, setControllerQrImageUrl] = useState("");
  const [adBreakShareQrImageUrl, setAdBreakShareQrImageUrl] = useState("");
  const [funFact, setFunFact] = useState(null);
  const [isQuestionActive, setIsQuestionActive] = useState(false);
  const [questionLeaderboardMetadata, setQuestionLeaderboardMetadata] =
    useState(null);
  const [questionIntroMetadata, setQuestionIntroMetadata] = useState(null);
  // Local start of a round's first question intro, for the round intro sound.
  const [roundIntroAudio, setRoundIntroAudio] = useState(null);
  // Local start of the answer reveal, for the answer reveal sound.
  const [answerRevealAudio, setAnswerRevealAudio] = useState(null);
  // Local start of the final standings, for the game over sound.
  const [gameOverAudio, setGameOverAudio] = useState(null);
  // The admin's live game-sound setting, from parlor-state. On until a snapshot
  // says otherwise, matching the server default.
  const [gameAudioAllowed, setGameAudioAllowed] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const [isUnauthorizedDisplay, setIsUnauthorizedDisplay] = useState(false);

  const { audioEnabled, soundEnabled, toggleSound, unlockAudio } = useQuestionTimerAudio({
    active: uiState === "game" && displayStage === DISPLAY_STAGES.ACTIVE_QUESTION && isQuestionActive,
    countdown: questionCountdownBar,
    questionKey: question?.id ?? question?.questionNumber ?? question?.questionText ?? "question",
    allowed: gameAudioAllowed,
    roundIntro: {
      active: uiState === "game" && displayStage === DISPLAY_STAGES.NEXT_QUESTION_INTRO,
      startTime: roundIntroAudio?.startTime,
      endTime: roundIntroAudio?.endTime,
      key: roundIntroAudio?.key,
    },
    // Runs its full length: the fun fact after the reveal still shows the answer.
    answerReveal: {
      active: uiState === "game" &&
        (displayStage === DISPLAY_STAGES.SHOW_ANSWER || displayStage === DISPLAY_STAGES.FUN_FACT),
      startTime: answerRevealAudio?.startTime,
      key: answerRevealAudio?.key,
    },
    // Final standings only, never the question or round boards before them,
    // and never past the wrap-up's own end.
    gameOver: {
      active: uiState === "game" && displayStage === DISPLAY_STAGES.GAME_OVER,
      startTime: gameOverAudio?.startTime,
      endTime: gameOverAudio?.endTime,
      key: gameOverAudio?.key,
    },
  });

  const serverOffsetRef = useRef(0);
  const serverTimeSyncRef = useRef({ bestRoundTripMs: Infinity, remainingSamples: 0 });
  const roundIntroRef = useRef(false);
  const roundIntroTimerRef = useRef(null);
  const isMountedRef = useRef(false);
  const queuedQuestions = useRef([]);
  const queuedStartTimer = useRef(null);
  const pendingCycleResetRef = useRef(false);
  const demoAppliedRef = useRef(false);
  const desiredParlorRef = useRef(null);
  const isUnauthorizedDisplayRef = useRef(false);
  const lastScoreDecayEventRef = useRef({ key: null, handledAt: 0 });
  const hasSeenScoreDecayStartedRef = useRef(false);
  const legacyQuestionStartListenerActiveRef = useRef(false);
  // Server timeline time of the newest snapshot stage applied, so a delayed or
  // repeated snapshot cannot take the display back to an older stage.
  const lastAppliedStageStampRef = useRef(null);
  // Notices a stage that has outlived its published end while the socket
  // still looks connected, and asks the server for the current stage again.
  const stageStallHandlersRef = useRef({});
  const stageStallMonitorRef = useRef(null);
  if (stageStallMonitorRef.current === null) {
    stageStallMonitorRef.current = createStageStallMonitor({
      onStall: (detail) => stageStallHandlersRef.current.onStall?.(detail) ?? false,
      onResyncTimeout: (detail) => stageStallHandlersRef.current.onResyncTimeout?.(detail),
      onRecovered: (detail) => stageStallHandlersRef.current.onRecovered?.(detail),
    });
  }

  useEffect(() => {
    isUnauthorizedDisplayRef.current = isUnauthorizedDisplay;
  }, [isUnauthorizedDisplay]);

  const reportDisplayTelemetry = useCallback((eventType, details = {}) => {
    const payload = {
      eventType,
      details,
      occurredAt: Date.now(),
    };

    try {
      socket.emit("display-client-diagnostic", payload);
    } catch {
      // Swallow emit errors to avoid cascading client-side failures.
    }

    console.warn("display-client-diagnostic", payload);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const preloadQrImages = async () => {
      const [controllerQr, adBreakQr] = await Promise.all([
        buildQrCodeImageUrl(CONTROLLER_QR_TARGET_URL),
        buildQrCodeImageUrl(AD_BREAK_SHARE_SMS_URI),
      ]);

      if (isCancelled) return;
      setControllerQrImageUrl(controllerQr);
      setAdBreakShareQrImageUrl(adBreakQr);
    };

    preloadQrImages();

    return () => {
      isCancelled = true;
    };
  }, []);

  const requestServerTime = useCallback((sampleCount = SERVER_TIME_SYNC_SAMPLE_COUNT) => {
    serverTimeSyncRef.current = { bestRoundTripMs: Infinity, remainingSamples: sampleCount };
    for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
      socket.emit("request-server-time", buildServerTimeRequestPayload());
    }
  }, []);

  const clearQuestionCountdownBar = useCallback(() => {
    setQuestionCountdownBar(hiddenQuestionCountdownBar);
  }, []);

  const clearRoundIntroTimer = useCallback(() => {
    if (roundIntroTimerRef.current !== null) {
      window.clearTimeout(roundIntroTimerRef.current);
      roundIntroTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearRoundIntroTimer();
      roundIntroRef.current = false;
      queuedStartTimer.current = null;
      stageStallMonitorRef.current?.reset();
    };
  }, [clearRoundIntroTimer]);

  const setQuestionCountdownBarWindow = useCallback((startTime, endTime) => {
    if (typeof startTime !== "number" || typeof endTime !== "number") {
      clearQuestionCountdownBar();
      return;
    }

    const durationMs = Math.max(0, endTime - startTime);
    setQuestionCountdownBar({
      visible: true,
      startTime,
      endTime,
      durationMs,
    });
  }, [clearQuestionCountdownBar]);

  const clearForNextCycle = useCallback(
    ({ resetStatus = true } = {}) => {
      setQuestion(null);
      setIsQuestionActive(false);
      setLeaderboard([]);
      setLeaderboards({ parlor: [], global: [] });
      setDisplayStage(DISPLAY_STAGES.IDLE);
      setLeaderboardTitle("");
      setCorrectIndex(null);
      setFunFact(null);
      setIsAdBreak(false);
      setAdTimeLeft(0);
      setAdBreakTimerLabel(DEFAULT_AD_BREAK_TIMER_LABEL);
      setIsRoundIntro(false);
      setUpcomingRoundNumber(null);
      roundIntroRef.current = false;
      clearRoundIntroTimer();
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
      setRoundIntroAudio(null);
      setAnswerRevealAudio(null);
      setGameOverAudio(null);
      queuedQuestions.current = [];
      queuedStartTimer.current = null;
      lastScoreDecayEventRef.current = { key: null, handledAt: 0 };
      hasSeenScoreDecayStartedRef.current = false;
      legacyQuestionStartListenerActiveRef.current = false;
      clearQuestionCountdownBar();
      if (resetStatus) setStatus("Waiting for game...");
    },
    [clearQuestionCountdownBar, clearRoundIntroTimer],
  );

  const maybeResetForNewCycle = useCallback(
    (metadata) => {
      if (
        pendingCycleResetRef.current &&
        metadata?.sequenceType === "question" &&
        (metadata?.questionNumber === 1 || metadata?.questionInRound === 1)
      ) {
        pendingCycleResetRef.current = false;
        clearForNextCycle({ resetStatus: false });
      }
    },
    [clearForNextCycle],
  );

  const joinParlor = useCallback((parlorCode, { resyncReason = null } = {}) => {
    if (!parlorCode) return;
    desiredParlorRef.current = parlorCode;
    socket.emit("join-parlor", {
      playerName: "Display",
      parlorCode,
      ...(resyncReason ? { resyncReason } : {}),
    });
  }, []);

  const processScoreDecayStarted = useCallback(
    (payload) => {
      if (!payload) return;
      const {
        question: stageQuestionPayload,
        remainingMs,
        duration,
        durationMs,
        startTime,
        endTime: targetEndTime,
        metadata,
      } = payload;
      const queuedQuestionPayload =
        queuedQuestions.current[queuedQuestions.current.length - 1] || null;
      const questionPayload = stageQuestionPayload || queuedQuestionPayload;
      maybeResetForNewCycle(metadata);
      queuedQuestions.current = [];
      clearRoundIntroTimer();
      setIsRoundIntro(false);
      setUpcomingRoundNumber(null);
      roundIntroRef.current = false;
      setDisplayStage(DISPLAY_STAGES.ACTIVE_QUESTION);
      setLeaderboardTitle("");
      setFunFact(null);
      setIsAdBreak(false);
      setCorrectIndex(null);
      if (questionPayload) {
        setQuestion(questionPayload);
        setIsQuestionActive(true);
      }
      setStatus(buildQuestionStatus(metadata, "Question In Progress"));
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);

      const resolvedDurationMs = resolveStageDurationMs({
        remainingMs,
        durationMs,
        duration,
      });
      const normalizedStartTime = normalizeMsTimestamp(startTime);
      const normalizedTargetEndTime = normalizeMsTimestamp(targetEndTime);
      const normalizedDurationMs = normalizeMsDuration(resolvedDurationMs);
      const localStartTime =
        typeof normalizedStartTime === "number"
          ? toLocalTime(normalizedStartTime, serverOffsetRef.current)
          : null;
      const localEndTime =
        typeof normalizedTargetEndTime === "number"
          ? toLocalTime(normalizedTargetEndTime, serverOffsetRef.current)
          : typeof localStartTime === "number" &&
              typeof normalizedDurationMs === "number"
            ? localStartTime + normalizedDurationMs
            : typeof normalizedDurationMs === "number"
              ? Date.now() + normalizedDurationMs
              : null;

      if (typeof localEndTime === "number") {
        if (typeof localStartTime === "number") {
          setQuestionCountdownBarWindow(localStartTime, localEndTime);
        } else if (typeof normalizedDurationMs === "number") {
          setQuestionCountdownBarWindow(localEndTime - normalizedDurationMs, localEndTime);
        }
      }

      queuedStartTimer.current = null;
    },
    [
      maybeResetForNewCycle,
      setQuestionCountdownBarWindow,
      clearRoundIntroTimer,
    ],
  );

  const completeRoundIntro = useCallback(() => {
    clearRoundIntroTimer();

    const queuedPayload = queuedStartTimer.current;
    queuedStartTimer.current = null;
    roundIntroRef.current = false;

    if (!isMountedRef.current) return;

    setIsRoundIntro(false);
    setUpcomingRoundNumber(null);

    if (queuedPayload) {
      processScoreDecayStarted(queuedPayload);
    }
  }, [
    clearRoundIntroTimer,
    processScoreDecayStarted,
    setIsRoundIntro,
    setUpcomingRoundNumber,
  ]);

  const startRoundIntroTimer = useCallback(() => {
    clearRoundIntroTimer();
    roundIntroTimerRef.current = window.setTimeout(
      completeRoundIntro,
      ROUND_INTRO_DURATION_MS,
    );
  }, [clearRoundIntroTimer, completeRoundIntro]);

  const handleEnterParlorSelection = useCallback(() => {
    unlockAudio();
    setParlorStatusMessage("");
    setUiState("parlor");
    setDisplayStage(DISPLAY_STAGES.PARLOR);
  }, [unlockAudio]);

  // The Welcome and Parlor screens open with their main button focused -
  // Play Now! and Join Public Parlor - so a TV remote starts there. Otherwise
  // focus is on the page body: at load, and whenever the focused button
  // unmounts as the screen changes. The first D-pad press then lands
  // wherever spatial navigation happens to guess.
  const playNowButtonRef = useRef(null);
  const joinPublicParlorButtonRef = useRef(null);
  useEffect(() => {
    if (uiState === "welcome") {
      playNowButtonRef.current?.focus();
    } else if (uiState === "parlor") {
      joinPublicParlorButtonRef.current?.focus();
    }
  }, [uiState]);

  const handleJoinDefaultParlor = useCallback(() => {
    unlockAudio();
    setParlorStatusMessage("");
    pendingCycleResetRef.current = false;
    lastAppliedStageStampRef.current = null;
    clearForNextCycle({ resetStatus: true });
    joinParlor(DEFAULT_PARLOR_CODE);
    requestServerTime();
    setUiState("game");
    setDisplayStage(DISPLAY_STAGES.IDLE);
  }, [clearForNextCycle, joinParlor, requestServerTime, unlockAudio]);

  const handleParlorCodeClick = useCallback(() => {
    setParlorStatusMessage(
      "Private parlors are coming soon. Join the public parlor to start playing.",
    );
  }, []);

  const handleReturnToWelcome = useCallback(() => {
    if (desiredParlorRef.current) {
      socket.emit("leave-parlor", {
        playerName: "Display",
        parlorCode: desiredParlorRef.current,
      });
      desiredParlorRef.current = null;
    }
    stageStallMonitorRef.current.reset();
    lastAppliedStageStampRef.current = null;
    setParlorStatusMessage("");
    pendingCycleResetRef.current = false;
    clearForNextCycle({ resetStatus: true });
    setUiState("welcome");
    setDisplayStage(DISPLAY_STAGES.WELCOME);
  }, [clearForNextCycle]);

  // The effect below registers socket listeners on mount and cleans up on unmount.
  // It uses the stable callbacks declared above; include them in the dependency
  // array so the linter is satisfied. These callbacks are memoized with useCallback
  // and will not change during normal operation, so re-registration won't occur.

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (demoAppliedRef.current) return;
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const demoMode = params.get("demo");
    if (
      demoMode !== "question" &&
      demoMode !== "question-leaderboard" &&
      demoMode !== "game-leaderboard"
    )
      return;

    demoAppliedRef.current = true;

    if (demoMode === "question") {
      setUiState("game");
      const sampleQuestion = {
        questionText:
          "This large sea, an arm of the Indian Ocean, is bordered by the Indochinese Peninsula, China, and the islands of the Philippines and Borneo.",
        options: [
          "The Sea of Japan",
          "The Yellow Sea",
          "The East China Sea",
          "South China Sea",
        ],
        roundNumber: 1,
        questionInRound: 3,
        questionNumber: 3,
      };
      const metadata = {
        roundNumber: sampleQuestion.roundNumber,
        questionInRound: sampleQuestion.questionInRound,
        questionNumber: sampleQuestion.questionNumber,
        sequenceType: "question",
      };

      setIsAdBreak(false);
      setIsRoundIntro(false);
      setQuestion(sampleQuestion);
      setIsQuestionActive(true);
      setStatus(buildQuestionStatus(metadata, "Question In Progress"));
      setLeaderboard([]);
      setDisplayStage(DISPLAY_STAGES.ACTIVE_QUESTION);
      setLeaderboardTitle("");
      setCorrectIndex(null);
      setFunFact(null);
      setQuestionLeaderboardMetadata(null);
      return;
    }

    const metadata = { roundNumber: 1, questionInRound: 7, questionNumber: 7 };
    const sampleLeaderboard = [
      { name: "Avalon", score: 1200 },
      { name: "Beacon", score: 900 },
      { name: "Cascade", score: 900 },
      { name: "Drift", score: 600 },
    ];
    const sampleGlobalLeaderboard = [
      { name: "Xavier", score: 5000 },
      { name: "Yolanda", score: 4500 },
      { name: "Zeke", score: 4000 },
      { name: "Avalon", score: 1200 },
    ];
    if (demoMode === "game-leaderboard") {
      setUiState("game");
      const title = buildStatus(LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100].heading, metadata);
      setIsAdBreak(false);
      setIsRoundIntro(false);
      setQuestion(null);
      setIsQuestionActive(false);
      setLeaderboard(sampleLeaderboard);
      setLeaderboards({ parlor: sampleLeaderboard, global: sampleGlobalLeaderboard });
      setDisplayStage(DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100);
      setLeaderboardTitle(title);
      setStatus(title);
      setCorrectIndex(null);
      setFunFact(null);
      setQuestionLeaderboardMetadata(null);
      return;
    }

    setUiState("game");
    setIsAdBreak(false);
    setIsRoundIntro(false);
    setQuestion(null);
    setIsQuestionActive(false);
    setLeaderboard(sampleLeaderboard);
    setDisplayStage(DISPLAY_STAGES.SHOW_QUESTION_RESULTS);
    setLeaderboards({ parlor: sampleLeaderboard, global: sampleGlobalLeaderboard });
    const title = buildStatus(
      LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.SHOW_QUESTION_RESULTS].heading,
      metadata,
    );
    setLeaderboardTitle(title);
    setStatus(title);
    setCorrectIndex(null);
    setFunFact(null);
    setQuestionLeaderboardMetadata({
      sequenceType: "question",
      roundNumber: metadata.roundNumber,
      questionNumber: metadata.questionNumber,
      questionInRound: metadata.questionInRound,
    });
  }, []);

  // Demo-only effect: runs once in development to seed sample UI. It uses setters and
  // helpers intentionally and should not re-run; suppress exhaustive-deps for clarity.
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    const SCORE_DECAY_DEDUPE_WINDOW_MS = 5000;
    const buildScoreDecayDedupeKey = (payload = {}) => {
      const question = payload?.question || null;
      const metadata = payload?.metadata || question || {};
      const round =
        metadata.roundNumber ?? question?.roundNumber ?? metadata.round ?? "na";
      const questionNumber =
        metadata.questionNumber ??
        question?.questionNumber ??
        metadata.questionInRound ??
        question?.questionInRound ??
        "na";
      const startTime = normalizeMsTimestamp(payload?.startTime);
      const endTime = normalizeMsTimestamp(
        payload?.endTime ?? payload?.answerDeadline ?? payload?.answerClosesAt,
      );
      return `${round}:${questionNumber}:${startTime ?? "na"}:${endTime ?? "na"}`;
    };
    const wasRecentlyHandledScoreDecay = (dedupeKey) => {
      const { key, handledAt } = lastScoreDecayEventRef.current;
      if (!dedupeKey || dedupeKey !== key) return false;
      return Date.now() - handledAt < SCORE_DECAY_DEDUPE_WINDOW_MS;
    };
    const markScoreDecayHandled = (dedupeKey) => {
      lastScoreDecayEventRef.current = {
        key: dedupeKey,
        handledAt: Date.now(),
      };
    };

    // The ad card and the round intro belong to their own stages, and every
    // later stage leaves them. Only the stages that normally follow them used
    // to, so a display that missed those stayed on the ad or round intro card
    // (both hide every other card) while the game played on underneath.
    const leaveTransitionalScreens = () => {
      clearRoundIntroTimer();
      roundIntroRef.current = false;
      queuedStartTimer.current = null;
      setIsRoundIntro(false);
      setUpcomingRoundNumber(null);
      setIsAdBreak(false);
    };

    // A question card left over from an earlier question must not be shown
    // under this stage's answer or fun fact.
    const dropStaleQuestion = (metadata) => {
      setQuestion((current) => (
        isQuestionForDifferentStage(current, metadata) ? null : current
      ));
    };

    const handleServerTime = (payload) => {
      const sample = resolveServerTimeOffsetSample(payload);
      if (!sample) return;
      stageStallMonitorRef.current.noteResponse();

      const syncState = serverTimeSyncRef.current;
      if (syncState.remainingSamples > 0) {
        syncState.remainingSamples = Math.max(0, syncState.remainingSamples - 1);
        if (sample.roundTripMs >= syncState.bestRoundTripMs) return;
        syncState.bestRoundTripMs = sample.roundTripMs;
      } else if (sample.roundTripMs >= syncState.bestRoundTripMs) {
        return;
      } else {
        syncState.bestRoundTripMs = sample.roundTripMs;
      }

      serverOffsetRef.current = sample.offset;
    };

    const handleNewQuestion = (q) => {
      if (!q) return;
      queuedQuestions.current = [q];
    };

    const handleAnswerSummary = ({ correctIndex }) => {
      setCorrectIndex(correctIndex);
    };

    const handleNextQuestionIntro = (payload) => {
      const metadata = payload?.metadata;
      maybeResetForNewCycle(metadata);
      clearRoundIntroTimer();
      queuedStartTimer.current = null;
      roundIntroRef.current = false;
      setQuestion(null);
      setIsQuestionActive(false);
      setCorrectIndex(null);
      setFunFact(null);
      setIsAdBreak(false);
      setIsRoundIntro(false);
      setUpcomingRoundNumber(null);
      setDisplayStage(DISPLAY_STAGES.NEXT_QUESTION_INTRO);
      setLeaderboardTitle("");
      setStatus(buildStatus("Get Ready", metadata));
      setQuestionIntroMetadata(metadata || null);
      setQuestionLeaderboardMetadata(null);
      setRoundIntroAudio(
        resolveRoundIntroAudio(payload, serverOffsetRef.current),
      );
    };

    const handleQuestionRevealed = (payload) => {
      const payloadQuestion = payload?.question || null;
      const queuedQuestionPayload =
        queuedQuestions.current[queuedQuestions.current.length - 1] || null;
      const questionPayload = payloadQuestion || queuedQuestionPayload;
      const metadata = payload?.metadata || questionPayload || null;

      maybeResetForNewCycle(metadata);
      if (questionPayload) {
        queuedQuestions.current = [questionPayload];
      }
      clearRoundIntroTimer();
      queuedStartTimer.current = null;
      setIsRoundIntro(false);
      setUpcomingRoundNumber(null);
      roundIntroRef.current = false;
      setQuestion(questionPayload || null);
      setDisplayStage(DISPLAY_STAGES.ACTIVE_QUESTION);
      setIsQuestionActive(!!questionPayload);
      setLeaderboardTitle("");
      setFunFact(null);
      setIsAdBreak(false);
      setCorrectIndex(null);
      setQuestionLeaderboardMetadata(metadata || null);
      setQuestionIntroMetadata(null);
      setStatus(buildQuestionStatus(metadata, "Question Ready"));

      const revealAt =
        normalizeMsTimestamp(payload?.revealAt) ??
        normalizeMsTimestamp(questionPayload?.revealAt) ??
        Date.now();
      const scoreDecayStartsAt =
        normalizeMsTimestamp(payload?.timerStart) ??
        normalizeMsTimestamp(payload?.scoreDecayStartsAt) ??
        normalizeMsTimestamp(questionPayload?.scoreDecayStartsAt) ??
        normalizeMsTimestamp(questionPayload?.startTime);
      const answerClosesAt =
        normalizeMsTimestamp(payload?.answerDeadline) ??
        normalizeMsTimestamp(payload?.answerClosesAt) ??
        normalizeMsTimestamp(questionPayload?.answerDeadline) ??
        normalizeMsTimestamp(questionPayload?.answerClosesAt);
      const scoringDuration =
        normalizeMsDuration(payload?.scoringDuration) ??
        normalizeMsDuration(questionPayload?.scoringDuration) ??
        normalizeMsDuration(questionPayload?.duration);
      const localStartTime =
        typeof scoreDecayStartsAt === "number"
          ? toLocalTime(scoreDecayStartsAt, serverOffsetRef.current)
          : null;
      const localEndTime =
        typeof answerClosesAt === "number"
          ? toLocalTime(answerClosesAt, serverOffsetRef.current)
          : typeof localStartTime === "number" && typeof scoringDuration === "number"
            ? localStartTime + scoringDuration
            : null;

      if (typeof localStartTime === "number" && typeof localEndTime === "number") {
        setQuestionCountdownBarWindow(localStartTime, localEndTime);
      } else if (questionPayload) {
        setQuestionCountdownBar({
          visible: true,
          startTime: payload?.revealAt ? toLocalTime(revealAt, serverOffsetRef.current) : revealAt,
          endTime: null,
          durationMs: null,
        });
      } else {
        clearQuestionCountdownBar();
      }
    };

    const handleScoreDecayStarted = (payload) => {
      const dedupeKey = buildScoreDecayDedupeKey(payload);
      if (wasRecentlyHandledScoreDecay(dedupeKey)) {
        return;
      }
      markScoreDecayHandled(dedupeKey);
      hasSeenScoreDecayStartedRef.current = true;
      if (legacyQuestionStartListenerActiveRef.current) {
        socket.off("question-start", handleLegacyQuestionStart);
        legacyQuestionStartListenerActiveRef.current = false;
      }
      if (roundIntroRef.current) {
        queuedStartTimer.current = payload;
        return;
      }
      processScoreDecayStarted(payload);
    };

    const handleLegacyQuestionStart = (questionPayload = null) => {
      if (hasSeenScoreDecayStartedRef.current) {
        return;
      }
      const question =
        questionPayload?.question ||
        (questionPayload && Object.keys(questionPayload).length > 0
          ? questionPayload
          : null);
      const metadata = {
        ...(questionPayload?.metadata || question || {}),
        sequenceType: "question",
      };
      const startTime = normalizeMsTimestamp(questionPayload?.startTime);
      const answerDeadline =
        normalizeMsTimestamp(questionPayload?.answerDeadline) ||
        normalizeMsTimestamp(questionPayload?.answerClosesAt);
      const duration = normalizeMsDuration(questionPayload?.duration);
      const dedupePayload = {
        question,
        metadata,
        startTime,
        endTime: answerDeadline,
      };
      const dedupeKey = buildScoreDecayDedupeKey(dedupePayload);
      if (wasRecentlyHandledScoreDecay(dedupeKey)) {
        return;
      }
      markScoreDecayHandled(dedupeKey);

      processScoreDecayStarted({
        question,
        metadata,
        startTime,
        endTime: answerDeadline,
        duration,
      });
    };

    const handleQuestionClosed = (payload = {}) => {
      const metadata = {
        ...(payload?.metadata || {}),
        sequenceType: "question",
      };

      maybeResetForNewCycle(metadata);
      leaveTransitionalScreens();
      if (payload?.question) {
        setQuestion(payload.question);
      } else {
        dropStaleQuestion(metadata);
      }
      setIsQuestionActive(false);
      setDisplayStage(DISPLAY_STAGES.SHOW_ANSWER);
      setStatus(buildQuestionStatus(metadata, "Answer Reveal"));
      setQuestionCountdownBar(getElapsedQuestionCountdownBar());
    };

    const handleShowAnswer = (payload) => {
      const {
        metadata,
        correctAnswer,
        correctAnswerIndex,
        question: answeredQuestion,
      } = payload || {};
      maybeResetForNewCycle(metadata);
      leaveTransitionalScreens();
      // Replays carry the question for a display that missed its reveal.
      if (answeredQuestion) {
        setQuestion(answeredQuestion);
      } else {
        dropStaleQuestion(metadata);
      }
      setIsQuestionActive(false);
      setDisplayStage(DISPLAY_STAGES.SHOW_ANSWER);
      setStatus(buildQuestionStatus(metadata, "Answer Reveal"));
      if (typeof correctAnswerIndex === "number") setCorrectIndex(correctAnswerIndex);
      if (typeof correctAnswer === "string") setFunFact(`Correct answer: ${correctAnswer}`);
      setQuestionCountdownBar(getElapsedQuestionCountdownBar());
      setAnswerRevealAudio(resolveStageAudio(payload, serverOffsetRef.current));
    };

    const handleFunFact = (payload = {}) => {
      const { metadata } = payload;
      maybeResetForNewCycle(metadata);
      leaveTransitionalScreens();
      // The fun fact is shown over the scored question's options. A display
      // that missed the reveal takes the question from the scored result.
      const scoredQuestion = resolveQuestionFromResult(payload.result);
      setQuestion((current) => {
        if (current && !isQuestionForDifferentStage(current, metadata)) return current;
        return scoredQuestion;
      });
      if (scoredQuestion && typeof payload.result?.correctIndex === "number") {
        setCorrectIndex(payload.result.correctIndex);
      }
      setIsQuestionActive(false);
      setDisplayStage(DISPLAY_STAGES.FUN_FACT);
      setLeaderboardTitle("");
      setIsAdBreak(false);
      setQuestionIntroMetadata(null);
      setQuestionLeaderboardMetadata(null);
      const selectedFunFact = pickRandomFunFact(payload) || null;
      setFunFact(selectedFunFact);
      setStatus(buildQuestionStatus(metadata, "Fun Fact"));
    };

    const handleQuestionLeaderboard = ({ metadata, result }) => {
      maybeResetForNewCycle(metadata);
      leaveTransitionalScreens();
      setQuestion(null);
      setIsQuestionActive(false);
      setIsAdBreak(false);
      setFunFact(null);
      setCorrectIndex(
        typeof result?.correctIndex === "number" ? result.correctIndex : null,
      );
      const derivedMetadata =
        (metadata || result)
          ? {
              ...(metadata || {}),
              sequenceType: metadata?.sequenceType || "question",
              roundNumber: metadata?.roundNumber ?? result?.roundNumber ?? null,
              questionNumber:
                metadata?.questionNumber ?? result?.questionNumber ?? null,
              questionIndex:
                metadata?.questionIndex ?? result?.questionIndex ?? null,
              questionInRound:
                metadata?.questionInRound ?? result?.questionInRound ?? null,
            }
          : null;
      const questionLeaderboard = buildQuestionLeaderboard(result);
      setLeaderboard(questionLeaderboard);
      setLeaderboards(
        result?.leaderboards || { parlor: questionLeaderboard, global: [] },
      );
      setDisplayStage(DISPLAY_STAGES.SHOW_QUESTION_RESULTS);
      const title = buildStatus(
        LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.SHOW_QUESTION_RESULTS].heading,
        derivedMetadata,
      );
      setLeaderboardTitle(title);
      setStatus(title);
      setQuestionLeaderboardMetadata(derivedMetadata || null);
      setQuestionIntroMetadata(null);
    };

    const handleGameLeaderboard = ({
      metadata,
      leaderboard: board,
      leaderboards: boardGroups,
    }) => {
      maybeResetForNewCycle(metadata);
      leaveTransitionalScreens();
      setQuestion(null);
      setIsQuestionActive(false);
      setIsAdBreak(false);
      setFunFact(null);
      setCorrectIndex(null);
      const derivedMetadata = {
        ...(metadata || {}),
        sequenceType: metadata?.sequenceType || "question",
        roundNumber: metadata?.roundNumber ?? boardGroups?.roundNumber ?? null,
        questionNumber:
          metadata?.questionNumber ?? boardGroups?.questionNumber ?? null,
        questionIndex:
          metadata?.questionIndex ?? boardGroups?.questionIndex ?? null,
        questionInRound:
          metadata?.questionInRound ?? boardGroups?.questionInRound ?? null,
      };
      setQuestionLeaderboardMetadata(derivedMetadata);
      setQuestionIntroMetadata(null);
      setLeaderboard(Array.isArray(board) ? board : []);
      setLeaderboards(
        boardGroups || {
          parlor: Array.isArray(board) ? board : [],
          global: Array.isArray(board) ? board : [],
        },
      );
      setDisplayStage(DISPLAY_STAGES.SHOW_QUESTION_GLOBAL_TOP_100);
      const title = buildStatus(
        LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.SHOW_QUESTION_GLOBAL_TOP_100].heading,
        derivedMetadata,
      );
      setLeaderboardTitle(title);
      setStatus(title);
    };

    const handleRoundLeaderboard = ({
      metadata,
      leaderboard: board,
      leaderboards: boardGroups,
    }) => {
      maybeResetForNewCycle(metadata);
      leaveTransitionalScreens();
      setQuestion(null);
      setIsQuestionActive(false);
      setIsAdBreak(false);
      setFunFact(null);
      setCorrectIndex(null);
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
      const parlorBoard = Array.isArray(board)
        ? board
        : Array.isArray(boardGroups?.parlor)
          ? boardGroups.parlor
          : [];
      setLeaderboard(parlorBoard);
      setLeaderboards(
        boardGroups || {
          parlor: parlorBoard,
          global: [],
        },
      );
      const title = buildStatus("Parlor Standings", metadata);
      setDisplayStage(DISPLAY_STAGES.SHOW_ROUND_PARLOR_STANDINGS);
      setLeaderboardTitle(title);
      setStatus(title);
    };

    const handleRoundGlobalTop100 = ({
      metadata,
      leaderboard: board,
      leaderboards: boardGroups,
    }) => {
      maybeResetForNewCycle(metadata);
      leaveTransitionalScreens();
      setQuestion(null);
      setIsQuestionActive(false);
      setIsAdBreak(false);
      setFunFact(null);
      setCorrectIndex(null);
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
      setLeaderboard(Array.isArray(board) ? board : []);
      setLeaderboards(
        boardGroups || {
          parlor: [],
          global: Array.isArray(board) ? board : [],
        },
      );
      setDisplayStage(DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100);
      const title = buildStatus(LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100].heading, metadata);
      setLeaderboardTitle(title);
      setStatus(title);
    };

    const handleStartAdBreak = ({
      remainingMs,
      durationMs,
      duration,
      adSlot,
      metadata,
      nextRoundNumber,
      roundNumber,
    } = {}) => {
      const effectiveDurationMs =
        typeof durationMs === "number"
          ? durationMs
          : typeof duration === "number"
            ? duration * 1000
            : null;
      setQuestion(null);
      setIsQuestionActive(false);
      setCorrectIndex(null);
      setFunFact(null);
      clearRoundIntroTimer();
      queuedStartTimer.current = null;
      roundIntroRef.current = false;
      setIsRoundIntro(false);
      setDisplayStage(DISPLAY_STAGES.AD_BREAK);
      setLeaderboardTitle("");
      setIsAdBreak(true);
      setUpcomingRoundNumber(
        resolveNextRoundNumber({
          metadata,
          nextRoundNumber,
          roundNumber,
        }),
      );
      setAdBreakTimerLabel(
        resolveAdBreakTimerLabel({
          adSlot,
          metadata,
          nextRoundNumber,
          roundNumber,
        }),
      );
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
      setStatus("Ad Break");
      if (typeof effectiveDurationMs === "number") {
        setAdTimeLeft(Math.ceil(effectiveDurationMs / 1000));
      } else {
        setAdTimeLeft(0);
      }
    };

    const handleAdBreakTick = ({ secondsRemaining }) => {
      setAdTimeLeft(secondsRemaining);
    };

    const handleAdBreakEnd = () => {
      setIsAdBreak(false);
      setAdTimeLeft(0);
      setAdBreakTimerLabel(DEFAULT_AD_BREAK_TIMER_LABEL);
      setStatus("Next Round Starting...");
      setDisplayStage(DISPLAY_STAGES.ROUND_INTRO);
      setIsRoundIntro(true);
      roundIntroRef.current = true;
      startRoundIntroTimer();
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
    };

    const handlePostGameWrapUp = (payload) => {
      const { leaderboard: board, leaderboards: boardGroups } = payload || {};
      setQuestion(null);
      setIsQuestionActive(false);
      setCorrectIndex(null);
      setFunFact(null);
      setIsAdBreak(false);
      setIsRoundIntro(false);
      setUpcomingRoundNumber(null);
      roundIntroRef.current = false;
      clearRoundIntroTimer();
      queuedQuestions.current = [];
      queuedStartTimer.current = null;
      pendingCycleResetRef.current = true;
      setLeaderboard(Array.isArray(board) ? board : []);
      setLeaderboards(
        boardGroups || {
          parlor: Array.isArray(board) ? board : [],
          global: Array.isArray(board) ? board : [],
        },
      );
      setPostGameViewRotationMs(resolvePostGameViewRotationMs(payload));
      setDisplayStage(DISPLAY_STAGES.GAME_OVER);
      const title = LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.GAME_OVER].heading;
      setLeaderboardTitle(title);
      setStatus(title);
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
      setGameOverAudio(keepGameOverAudio(resolveGameOverAudio(payload, serverOffsetRef.current)));
    };

    const handleGameStopped = () => {
      pendingCycleResetRef.current = false;
      clearForNextCycle({ resetStatus: false });
      setStatus("Game Stopped");
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
    };

    const handleGameOver = (payload) => {
      const { leaderboard: board, leaderboards: boardGroups } = payload || {};
      pendingCycleResetRef.current = true;
      setQuestion(null);
      setIsQuestionActive(false);
      setCorrectIndex(null);
      setFunFact(null);
      setIsAdBreak(false);
      setIsRoundIntro(false);
      setUpcomingRoundNumber(null);
      roundIntroRef.current = false;
      clearRoundIntroTimer();
      setLeaderboard(Array.isArray(board) ? board : []);
      setLeaderboards(
        boardGroups || {
          parlor: Array.isArray(board) ? board : [],
          global: Array.isArray(board) ? board : [],
        },
      );
      setPostGameViewRotationMs(POST_GAME_VIEW_ROTATION_MS);
      setDisplayStage(DISPLAY_STAGES.GAME_OVER);
      setLeaderboardTitle(LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.GAME_OVER].heading);
      setStatus("Game Over");
      setQuestionLeaderboardMetadata(null);
      setQuestionIntroMetadata(null);
      setGameOverAudio(keepGameOverAudio(resolveGameOverAudio(payload, serverOffsetRef.current)));
    };

    const handleParlorFull = ({ parlorCode: code, maxPlayers }) => {
      const limit = typeof maxPlayers === "number" ? maxPlayers : 100;
      const resolvedCode = code || desiredParlorRef.current || "default";
      clearForNextCycle({ resetStatus: true });
      setUiState("parlor");
      setDisplayStage(DISPLAY_STAGES.PARLOR);
      setParlorStatusMessage(
        `Parlor ${resolvedCode} is full (max ${limit} players). Please join another parlor.`,
      );
    };


    const handleParlorState = (snapshot) => {
      if (!snapshot || typeof snapshot !== "object") return;
      // A global admin setting, not part of any stage, so it applies even from
      // a snapshot whose stage is stale.
      if (typeof snapshot.gameAudioEnabled === "boolean") {
        setGameAudioAllowed(snapshot.gameAudioEnabled);
      }

      const stageRecord = snapshot.currentStage;
      const stageType = stageRecord?.type;
      const stagePayload = stageRecord?.payload || {};
      const remainingMs =
        typeof stageRecord?.remainingMs === "number" &&
        !Number.isNaN(stageRecord.remainingMs)
          ? Math.max(0, stageRecord.remainingMs)
          : null;

      stageStallMonitorRef.current.noteStage(stageRecord || null);
      // A snapshot of a stage older than one already shown (delayed, or
      // repeated from an earlier join) must not take the display back.
      if (isStageOlderThan(stageRecord, lastAppliedStageStampRef.current)) {
        return;
      }
      const stageStamp = resolveStageTimelineStamp(stageRecord);
      if (stageStamp !== null) {
        lastAppliedStageStampRef.current = stageStamp;
      }

      if (stageType === "game-stopped") {
        handleGameStopped();
        return;
      }

      // The ad applies only while it is the current stage. A snapshot whose
      // stage has moved on but which still carries an ad break used to win
      // over that stage and put the display back on the ad card.
      if (snapshot.adBreak?.payload && (!stageType || stageType === "start-ad-break")) {
        const adBreakPayload = {
          ...snapshot.adBreak.payload,
          durationMs:
            remainingMs ??
            (typeof snapshot.adBreak.remainingSeconds === "number"
              ? snapshot.adBreak.remainingSeconds * 1000
              : snapshot.adBreak.payload.durationMs),
        };
        handleStartAdBreak(adBreakPayload);
        if (typeof snapshot.adBreak.remainingSeconds === "number") {
          handleAdBreakTick({ secondsRemaining: snapshot.adBreak.remainingSeconds });
        }
        return;
      }

      const replayPayload = stagePayload;
      const stageMetadata =
        replayPayload?.metadata || stageRecord?.metadata || null;

      switch (stageType) {
        case GAME_START_INTRO_STAGE:
        case "next-question-intro":
          handleNextQuestionIntro({
            ...replayPayload,
            remainingMs,
          });
          return;
        case QUESTION_PHASES.REVEALED:
          handleQuestionRevealed({
            ...replayPayload,
            question: replayPayload?.question || snapshot.lastQuestion || null,
            metadata: {
              ...replayPayload?.metadata,
              sequenceType: "question",
            },
          });
          return;
        case QUESTION_PHASES.SCORE_DECAY_STARTED:
          handleScoreDecayStarted({
            ...replayPayload,
            question: replayPayload?.question || snapshot.lastQuestion || null,
            remainingMs,
          });
          return;
        case QUESTION_PHASES.CLOSED:
          handleQuestionClosed({
            ...replayPayload,
            question: replayPayload?.question || snapshot.lastQuestion || null,
            metadata: {
              ...(replayPayload?.metadata || {}),
              sequenceType: "question",
            },
          });
          return;
        case QUESTION_PHASES.SHOW_ANSWER:
          handleShowAnswer({
            ...replayPayload,
            remainingMs,
          });
          return;
        case "show-fun-fact":
          handleFunFact({
            ...replayPayload,
            remainingMs,
          });
          return;
        case "show-question-results":
          handleQuestionLeaderboard({
            ...replayPayload,
            metadata: stageMetadata,
            remainingMs,
          });
          return;
        case "show-question-global-top100":
          handleGameLeaderboard({
            ...replayPayload,
            metadata: stageMetadata,
            remainingMs,
          });
          return;
        case "show-round-parlor-standings":
          handleRoundLeaderboard({
            ...replayPayload,
            remainingMs,
          });
          return;
        case "show-round-global-top100":
          handleRoundGlobalTop100({
            ...replayPayload,
            remainingMs,
          });
          return;
        case "show-post-game-wrap-up":
          handlePostGameWrapUp({
            ...replayPayload,
            remainingMs,
          });
          return;
        default:
          break;
      }

      if (snapshot.isCycling === false) {
        return;
      }

      if (snapshot.lastQuestion) {
        const activeWindow = snapshot.activeQuestionWindow || {};
        handleLegacyQuestionStart({
          question: snapshot.lastQuestion,
          metadata: {
            sequenceType: "question",
            roundNumber:
              snapshot.lastQuestion.roundNumber ??
              snapshot.currentRoundNumber ??
              null,
            questionNumber: snapshot.lastQuestion.questionNumber ?? null,
            questionInRound: snapshot.lastQuestion.questionInRound ?? null,
            totalQuestions: snapshot.totalQuestions ?? null,
          },
          startTime: activeWindow.start ?? null,
          answerDeadline:
            activeWindow.end ??
            snapshot.lastQuestion.answerDeadline ??
            snapshot.lastQuestion.answerClosesAt ??
            null,
          duration:
            typeof activeWindow.start === "number" &&
            typeof activeWindow.end === "number"
              ? Math.max(0, activeWindow.end - activeWindow.start)
              : snapshot.lastQuestion.duration ?? null,
        });
      }
    };

    stageStallHandlersRef.current = {
      // The stage on screen should have ended a while ago and nothing newer
      // has arrived. Ask for the current stage through the ordinary join; the
      // snapshot and replay it answers with go through the handlers above.
      onStall: (detail) => {
        if (!socket.connected || !desiredParlorRef.current || isUnauthorizedDisplayRef.current) {
          return false;
        }
        reportDisplayTelemetry("stale-stage-detected", detail);
        socket.emit("request-server-time", buildServerTimeRequestPayload());
        joinParlor(desiredParlorRef.current, { resyncReason: "stale-stage" });
        return true;
      },
      // Connected in name only: nothing came back. Rebuild the connection;
      // the connect handler rejoins.
      onResyncTimeout: (detail) => {
        if (!socket.connected) return;
        reportDisplayTelemetry("stale-stage-reconnect", detail);
        socket.disconnect();
        socket.connect();
      },
      onRecovered: (detail) => {
        reportDisplayTelemetry("stale-stage-recovered", detail);
      },
    };

    socket.on("server-time", handleServerTime);
    socket.on("new-question", handleNewQuestion);
    socket.on("question", handleNewQuestion);
    socket.on("answer-summary", handleAnswerSummary);
    socket.on(QUESTION_PHASES.SHOW_ANSWER, handleShowAnswer);
    socket.on(GAME_START_INTRO_STAGE, handleNextQuestionIntro);
    socket.on("next-question-intro", handleNextQuestionIntro);
    socket.on(QUESTION_PHASES.REVEALED, handleQuestionRevealed);
    socket.on(QUESTION_PHASES.SCORE_DECAY_STARTED, handleScoreDecayStarted);
    if (!hasSeenScoreDecayStartedRef.current) {
      socket.on("question-start", handleLegacyQuestionStart);
      legacyQuestionStartListenerActiveRef.current = true;
    }
    socket.on(QUESTION_PHASES.CLOSED, handleQuestionClosed);
    socket.on("show-fun-fact", handleFunFact);
    socket.on("show-question-results", handleQuestionLeaderboard);
    socket.on("show-question-global-top100", handleGameLeaderboard);
    socket.on("show-round-parlor-standings", handleRoundLeaderboard);
    socket.on("show-round-global-top100", handleRoundGlobalTop100);
    socket.on("start-ad-break", handleStartAdBreak);
    socket.on("ad-break-start", handleStartAdBreak);
    socket.on("ad-break-tick", handleAdBreakTick);
    socket.on("ad-break-end", handleAdBreakEnd);
    socket.on("show-post-game-wrap-up", handlePostGameWrapUp);
    socket.on("game-stopped", handleGameStopped);
    socket.on("game-over", handleGameOver);
    socket.on("parlor-state", handleParlorState);
    socket.on("parlor-full", handleParlorFull);

    const handleConnect = () => {
      setConnectionError(null);
      setIsUnauthorizedDisplay(false);
      if (desiredParlorRef.current) {
        joinParlor(desiredParlorRef.current);
      }
      requestServerTime();
    };
    const handleConnectError = (error) => {
      const errorType = normalizeSocketErrorType(error);
      const unauthorized = isUnauthorizedKeyError(errorType);
      if (unauthorized) {
        setUiState("game");
        setIsUnauthorizedDisplay(true);
        setConnectionError({
          type: errorType,
          message:
            "This display connection was rejected by the server.",
        });
        setStatus("Display unauthorized");
      } else {
        setIsUnauthorizedDisplay(false);
        setConnectionError({
          type: errorType,
          message: "Connection error. Retrying…",
        });
      }
      console.error("display-socket-diagnostic", {
        type: unauthorized ? "auth-key-failure" : errorType || "unknown",
      });
    };
    const handleDisconnect = (reason) => {
      // Socket.IO is reconnecting; the rejoin's snapshot starts a fresh watch.
      stageStallMonitorRef.current.reset();
      if (isUnauthorizedDisplayRef.current) return;
      setConnectionError({
        type: "disconnect",
        message: "Disconnected from game server. Attempting to reconnect…",
      });
      setStatus("Reconnecting to game server…");
      console.info("display-socket-diagnostic", {
        type: "disconnect",
      });
      if (reason === "io server disconnect") {
        socket.connect();
      }
    };
    const handleReconnectAttempt = () => {
      if (isUnauthorizedDisplayRef.current) return;
      setConnectionError({
        type: "reconnect-attempt",
        message: "Attempting to reconnect…",
      });
      setStatus("Reconnecting to game server…");
    };
    const handleReconnect = () => {
      if (isUnauthorizedDisplayRef.current) return;
      setConnectionError(null);
      setStatus("Connected");
    };
    const handleReconnectError = (error) => {
      if (isUnauthorizedDisplayRef.current) return;
      const errorType = normalizeSocketErrorType(error);
      setConnectionError({
        type: errorType || "reconnect-error",
        message: "Reconnect failed. Retrying…",
      });
      console.warn("display-socket-diagnostic", {
        type: errorType || "reconnect-error",
      });
    };
    const socketManager = socket.io;
    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.on("disconnect", handleDisconnect);
    if (socketManager?.on) {
      socketManager.on("reconnect_attempt", handleReconnectAttempt);
      socketManager.on("reconnect", handleReconnect);
      socketManager.on("reconnect_error", handleReconnectError);
    }
    handleConnect();

    return () => {
      socket.off("server-time", handleServerTime);
      socket.off("new-question", handleNewQuestion);
      socket.off("question", handleNewQuestion);
      socket.off("answer-summary", handleAnswerSummary);
      socket.off(QUESTION_PHASES.SHOW_ANSWER, handleShowAnswer);
      socket.off(GAME_START_INTRO_STAGE, handleNextQuestionIntro);
      socket.off("next-question-intro", handleNextQuestionIntro);
      socket.off(QUESTION_PHASES.REVEALED, handleQuestionRevealed);
      socket.off(QUESTION_PHASES.SCORE_DECAY_STARTED, handleScoreDecayStarted);
      socket.off("question-start", handleLegacyQuestionStart);
      legacyQuestionStartListenerActiveRef.current = false;
      socket.off(QUESTION_PHASES.CLOSED, handleQuestionClosed);
      socket.off("show-fun-fact", handleFunFact);
      socket.off("show-question-results", handleQuestionLeaderboard);
      socket.off("show-question-global-top100", handleGameLeaderboard);
      socket.off("show-round-parlor-standings", handleRoundLeaderboard);
      socket.off("show-round-global-top100", handleRoundGlobalTop100);
      socket.off("start-ad-break", handleStartAdBreak);
      socket.off("ad-break-start", handleStartAdBreak);
      socket.off("ad-break-tick", handleAdBreakTick);
      socket.off("ad-break-end", handleAdBreakEnd);
      socket.off("show-post-game-wrap-up", handlePostGameWrapUp);
      socket.off("game-stopped", handleGameStopped);
      socket.off("game-over", handleGameOver);
      socket.off("parlor-state", handleParlorState);
      socket.off("parlor-full", handleParlorFull);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
      if (socketManager?.off) {
        socketManager.off("reconnect_attempt", handleReconnectAttempt);
        socketManager.off("reconnect", handleReconnect);
        socketManager.off("reconnect_error", handleReconnectError);
      }
    };
  }, [
    clearForNextCycle,
    maybeResetForNewCycle,
    processScoreDecayStarted,
    clearQuestionCountdownBar,
    clearRoundIntroTimer,
    setQuestionCountdownBarWindow,
    startRoundIntroTimer,
    joinParlor,
    requestServerTime,
    reportDisplayTelemetry,
    setIsRoundIntro,
    setUpcomingRoundNumber,
  ]);

  const parlorLeaderboard = (leaderboards.parlor || []).filter(
    (p) => p.name !== "Host" && p.name !== "Display",
  );
  const globalLeaderboard = (leaderboards.global || []).filter(
    (p) => p.name !== "Host" && p.name !== "Display",
  );
  const isLeaderboardStage =
    displayStage === DISPLAY_STAGES.SHOW_QUESTION_RESULTS ||
    displayStage === DISPLAY_STAGES.SHOW_QUESTION_GLOBAL_TOP_100 ||
    displayStage === DISPLAY_STAGES.SHOW_ROUND_PARLOR_STANDINGS ||
    displayStage === DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100 ||
    displayStage === DISPLAY_STAGES.GAME_OVER;
  const showLeaderboardCard = !isAdBreak && !isRoundIntro && isLeaderboardStage;

  // Post-game only (game-over): rotate the Parlor final standings before the
  // Global final standings. Both always get a turn, even when one is empty,
  // as the question and round boards do: dropping an empty Parlor board left
  // Global Standings on screen for the whole wrap-up. Presentation-only — the
  // server stays authoritative for the leaderboard data itself.
  const isPostGameStage = displayStage === DISPLAY_STAGES.GAME_OVER;
  const postGameViews = isPostGameStage
    ? [
        {
          key: "parlor",
          heading: "Parlor Standings",
          entries: parlorLeaderboard,
        },
        {
          key: "global",
          heading: "Global Standings",
          entries: globalLeaderboard,
        },
      ]
    : [];
  const postGameViewCount = postGameViews.length;
  const activePostGameView =
    postGameViewCount > 0
      ? postGameViews[postGameViewIndex % postGameViewCount]
      : null;

  useEffect(() => {
    if (!isPostGameStage || postGameViewCount <= 1) {
      setPostGameViewIndex(0);
      return undefined;
    }

    const rotation = setInterval(() => {
      setPostGameViewIndex((prev) => (prev + 1) % postGameViewCount);
    }, postGameViewRotationMs);

    return () => clearInterval(rotation);
  }, [isPostGameStage, postGameViewCount, postGameViewRotationMs]);
  const showQuestionIntroCard =
    displayStage === DISPLAY_STAGES.NEXT_QUESTION_INTRO &&
    !isAdBreak &&
    !isRoundIntro &&
    Boolean(questionIntroMetadata);
  const showStatusBanner =
    displayStage !== DISPLAY_STAGES.AD_BREAK &&
    !isRoundIntro &&
    !isLeaderboardStage &&
    !showQuestionIntroCard;
  const leaderboardScreenCopy =
    LEADERBOARD_SCREEN_COPY[displayStage] ||
    LEADERBOARD_SCREEN_COPY[DISPLAY_STAGES.SHOW_QUESTION_RESULTS];
  const currentLeaderboard =
    leaderboardScreenCopy.leaderboardView === "question"
      ? leaderboard
      : leaderboardScreenCopy.leaderboardView === "global"
        ? globalLeaderboard
        : parlorLeaderboard;
  const isQuestionLeaderboardStage =
    displayStage === DISPLAY_STAGES.SHOW_QUESTION_RESULTS ||
    displayStage === DISPLAY_STAGES.SHOW_QUESTION_GLOBAL_TOP_100;
  const headingQuestionNumber = resolveOverallQuestionNumber(
    questionLeaderboardMetadata,
  );
  const headingQuestionLabel = headingQuestionNumber
    ? `Question ${headingQuestionNumber}`
    : "";
  // Round standings name only the round they close ("Round 2"), even when the
  // stage title also carries a question number.
  const leaderboardRoundLabel =
    (leaderboardTitle || "")
      .split("•")
      .map((part) => part.trim())
      .slice(1)
      .find((part) => /^Round \d+$/.test(part)) || "";
  const leaderboardSubtitle = isQuestionLeaderboardStage
    ? headingQuestionLabel || leaderboardScreenCopy.fallbackSubtitle
    : displayStage === DISPLAY_STAGES.GAME_OVER
      ? FINAL_RESULTS_SUBTITLE
      : leaderboardRoundLabel || leaderboardScreenCopy.fallbackSubtitle;
  const isWelcome = uiState === "welcome";
  const isParlorSelection = uiState === "parlor";
  const isGameUi = uiState === "game";
  const gameplayTextFadeClass =
    isGameUi && areDisplayAnimationsEnabled()
      ? "display-stage-text-fade"
      : "";
  const upcomingQuestionLabel = questionIntroMetadata
    ? formatQuestionLabel({
        ...questionIntroMetadata,
        sequenceType: "question",
      })
    : "";
  const questionOptions = Array.isArray(question?.options)
    ? question.options
    : [];
  const isFunFactDisplay =
    displayStage === DISPLAY_STAGES.FUN_FACT && Boolean(funFact);
  const isAnswerRevealActive =
    Number.isInteger(correctIndex) &&
    (displayStage === DISPLAY_STAGES.ACTIVE_QUESTION ||
      displayStage === DISPLAY_STAGES.SHOW_ANSWER ||
      displayStage === DISPLAY_STAGES.FUN_FACT) &&
    !isQuestionActive;
  const showQuestionCard =
    ((displayStage === DISPLAY_STAGES.ACTIVE_QUESTION ||
      displayStage === DISPLAY_STAGES.SHOW_ANSWER) &&
      Boolean(question)) ||
    isFunFactDisplay;
  const upcomingRoundLabel = upcomingRoundNumber
    ? `Round ${upcomingRoundNumber}`
    : "";
  const bannerRoundLabel = question?.roundNumber
    ? `Round ${question.roundNumber}`
    : "";

  return (
    <div className="app-container">
      <DisplayErrorBoundary
        onError={({ error, componentStack }) => {
          reportDisplayTelemetry("display-render-crash", {
            message: error?.message || "unknown-render-error",
            componentStack,
          });
        }}
      >
        <main
          className={`display-content${showQuestionIntroCard ? " display-content--intro" : ""}`}
        >
        {isWelcome && (
          <section className="card display-screen-card welcome-card">
            <h2 className="welcome-title">Welcome to Brain Battle Live!</h2>
            {HAS_CONTROLLER_QR && (
              <div className="display-tile game-code-tile">
                <div className="welcome-qr">
                  {controllerQrImageUrl ? (
                    <img
                      src={controllerQrImageUrl}
                      alt="Scan to open the controller on your phone"
                    />
                  ) : (
                    <div className="qr-placeholder" aria-hidden="true" />
                  )}
                </div>
                <p className="welcome-qr-caption">
                  Scan code to launch the controller from your mobile device!
                </p>
              </div>
            )}
            <div className="welcome-actions">
              <button
                ref={playNowButtonRef}
                type="button"
                className="parlor-button parlor-button--welcome"
                onClick={handleEnterParlorSelection}
              >
                Play Now!
              </button>
            </div>
          </section>
        )}

        {isParlorSelection && (
          <section className="card display-screen-card parlor-card">
            <DisplayWordmark className="parlor-card__wordmark" />
            <h2>Join a Parlor</h2>
            <p className="parlor-hint">Click an option to continue.</p>
            <div className="parlor-options">
              <button
                ref={joinPublicParlorButtonRef}
                type="button"
                className="parlor-option-card parlor-option-card--primary"
                onClick={handleJoinDefaultParlor}
              >
                <span className="parlor-option-title">Join Public Parlor</span>
                <span className="parlor-option-subtitle">
                  Start playing right away.
                </span>
              </button>
              <button
                type="button"
                className="parlor-option-card"
                onClick={handleParlorCodeClick}
              >
                <span className="parlor-option-title">Join Private Parlor</span>
                <span className="parlor-option-subtitle">
                  Private parlors coming soon.
                </span>
              </button>
              <button
                type="button"
                className="parlor-option-card"
                onClick={handleParlorCodeClick}
              >
                <span className="parlor-option-title">Create a Private Parlor</span>
                <span className="parlor-option-subtitle">
                  Private parlors coming soon.
                </span>
              </button>
            </div>
            {parlorStatusMessage && (
              <p className="parlor-status">{parlorStatusMessage}</p>
            )}
            <div className="parlor-actions">
              {audioEnabled && (
                <button
                  type="button"
                  className="parlor-button parlor-button--secondary"
                  aria-label="Game sound"
                  aria-pressed={soundEnabled}
                  onClick={toggleSound}
                >
                  Sound: {soundEnabled ? "On" : "Off"}
                </button>
              )}
              <button
                type="button"
                className="parlor-button parlor-button--secondary"
                onClick={handleReturnToWelcome}
              >
                Back to Welcome
              </button>
            </div>
          </section>
        )}

        {isGameUi && (
          <>
            {showStatusBanner && (
              <section className="status-banner card display-screen-card" data-testid="status-banner">
                <div className="status-header">
                  <p
                    className={buildClassName(
                      "status-banner__round",
                      gameplayTextFadeClass,
                    )}
                  >
                    {bannerRoundLabel}
                  </p>
                  <DisplayWordmark
                    className="status-banner__wordmark"
                    animateText={Boolean(gameplayTextFadeClass)}
                  />
                  <h2
                    className={buildClassName(
                      "status-banner__status",
                      gameplayTextFadeClass,
                    )}
                  >
                    {status}
                  </h2>
                </div>
              </section>
            )}
            {isUnauthorizedDisplay && (
              <section
                className="card display-screen-card connection-error-card"
                data-testid="unauthorized-display-card"
              >
                <h2>🚫 Display Connection Rejected</h2>
                <p>
                  This display cannot join the game because the server rejected
                  the display socket connection.
                </p>
                <p className="connection-error-card__hint">
                  {connectionError?.message ||
                    "Waiting to reconnect."}
                </p>
              </section>
            )}
            {connectionError && !isUnauthorizedDisplay && (
              <section className="card display-screen-card connection-error-card" data-testid="connection-error-card">
                <h2>⚠️ Connection Issue</h2>
                <p>{connectionError.message}</p>
              </section>
            )}

            <LiveDisplayStageTransition
              uiState={uiState}
              displayStage={displayStage}
            >
              {isRoundIntro && (
                <section
                  className="card display-screen-card intro-screen round-intro-screen"
                  data-testid="round-intro-screen"
                  aria-label={upcomingRoundLabel || "Next Round"}
                >
                  <div className="intro-screen__header">
                    <DisplayWordmark animateText={Boolean(gameplayTextFadeClass)} />
                  </div>
                  <div className="intro-screen__content">
                    <h2 className={gameplayTextFadeClass}>
                      Get Ready for the Next Round!
                    </h2>
                    {upcomingRoundLabel && (
                      <p
                        className={buildClassName(
                          "intro-screen__label",
                          gameplayTextFadeClass,
                        )}
                      >
                        {upcomingRoundLabel}
                      </p>
                    )}
                  </div>
                </section>
              )}

              {showQuestionIntroCard && (
                <section
                  className="card display-screen-card intro-screen"
                  data-testid="question-intro-screen"
                >
                  <div className="intro-screen__header">
                    <DisplayWordmark animateText={Boolean(gameplayTextFadeClass)} />
                  </div>
                  <div className="intro-screen__content">
                    <h2 className={gameplayTextFadeClass}>
                      Get Ready for the Next Question!
                    </h2>
                    {upcomingQuestionLabel && (
                      <p
                        className={buildClassName(
                          "intro-screen__label",
                          gameplayTextFadeClass,
                        )}
                      >
                        {upcomingQuestionLabel}
                      </p>
                    )}
                  </div>
                </section>
              )}

              {showQuestionCard &&
                !isAdBreak &&
                !isRoundIntro && (
                <section className="card display-screen-card question-card" data-testid="question-card">
                  <div className="question-card__title-slot">
                    {/* The fun fact replaces the question in the same box, so a
                        label says which one is on screen. */}
                    {isFunFactDisplay && (
                      <p
                        className={buildClassName(
                          "question-card__fun-fact-label",
                          gameplayTextFadeClass,
                        )}
                      >
                        Fun Fact
                      </p>
                    )}
                    <AutoFitText
                      as="h2"
                      className={buildClassName(
                        "fit-text question-card-title",
                        isFunFactDisplay ? "question-card-title--fun-fact" : "",
                      )}
                      innerClassName={buildClassName(
                        "question-card-title__text",
                        gameplayTextFadeClass,
                      )}
                      {...(isFunFactDisplay ? FUN_FACT_TITLE_FIT : QUESTION_TITLE_FIT)}
                    >
                      {isFunFactDisplay ? funFact : question.questionText}
                    </AutoFitText>
                  </div>
                  <div
                    className={
                      "question-countdown-bar-slot " +
                      (questionCountdownBar.visible
                        ? ""
                        : "question-countdown-bar-slot--hidden")
                    }
                  >
                    <QuestionCountdownBar countdown={questionCountdownBar} />
                  </div>
                  {/* Shared answer index contract (controller + display):
                      0 = top-left, 1 = top-right, 2 = bottom-left, 3 = bottom-right.
                      Keep options.map(...) order unchanged so correctIndex uses the
                      same unmodified index space. */}
                  <div
                    className={`question-grid${isAnswerRevealActive ? " question-grid--answer-reveal" : ""}`}
                  >
                    {questionOptions.map((opt, i) => {
                      const isCorrectOption = isAnswerRevealActive && correctIndex === i;
                      const shouldHideIncorrectOption =
                        isAnswerRevealActive && !isCorrectOption;
                      const optionClassName = [
                        "option-card",
                        isCorrectOption ? "correct-option" : "",
                        shouldHideIncorrectOption ? "incorrect-option-hidden" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <AutoFitText
                          key={i}
                          as="div"
                          data-testid={`answer-option-${i}`}
                          data-answer-label={ANSWER_LABELS[i] || String(i + 1)}
                          className={`fit-text ${optionClassName}`}
                          innerClassName={buildClassName(
                            "option-text",
                            gameplayTextFadeClass,
                          )}
                          trailing={
                            isCorrectOption ? (
                              <span
                                className="option-card__correct-badge"
                                role="img"
                                aria-label="Correct"
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                  <polyline points="4.5 12.5 10 18 19.5 6.5" />
                                </svg>
                              </span>
                            ) : null
                          }
                          fitGroup={answerFitGroup}
                          {...ANSWER_OPTION_FIT}
                        >
                          {opt}
                        </AutoFitText>
                      );
                    })}
                  </div>
                </section>
              )}

              {showLeaderboardCard && isPostGameStage && activePostGameView && (
                <LeaderboardBoard
                  key={activePostGameView.key}
                  testId="post-game-leaderboard-card"
                  heading={activePostGameView.heading}
                  subtitle={FINAL_RESULTS_SUBTITLE}
                  entries={activePostGameView.entries}
                  entryKeyScope={`post-game-${activePostGameView.key}`}
                  animateText={Boolean(gameplayTextFadeClass)}
                />
              )}

              {showLeaderboardCard && !(isPostGameStage && activePostGameView) && (
                <LeaderboardBoard
                  heading={leaderboardScreenCopy.heading}
                  subtitle={leaderboardSubtitle}
                  entries={currentLeaderboard}
                  entryKeyScope={displayStage}
                  animateText={Boolean(gameplayTextFadeClass)}
                />
              )}

              {isAdBreak && (
                <section className="card display-screen-card ad-break-box" data-testid="ad-break-card">
                  <div className="ad-break-header">
                    <DisplayWordmark animateText={Boolean(gameplayTextFadeClass)} />
                  </div>
                  {HAS_AD_BREAK_SHARE_QR && (
                    <div className="ad-break-share ad-break-share--text">
                      <div className="ad-break-share-qr-slot">
                        <div className="ad-break-share-qr">
                          {adBreakShareQrImageUrl ? (
                            <img
                              src={adBreakShareQrImageUrl}
                              alt="Scan to open a text message you can share with friends"
                            />
                          ) : (
                            <div className="qr-placeholder" aria-hidden="true" />
                          )}
                        </div>
                      </div>
                      <p
                        className={buildClassName(
                          "ad-break-share-caption",
                          gameplayTextFadeClass,
                        )}
                      >
                        Scan to open a text message you can share with friends.
                      </p>
                    </div>
                  )}
                  <div className="ad-break-copy">
                    <p
                      className={buildClassName(
                        "ad-break-message",
                        gameplayTextFadeClass,
                      )}
                    >
                      {AD_BREAK_COPY}
                    </p>
                  </div>
                  {HAS_CONTROLLER_QR && (
                    <div className="ad-break-share ad-break-share--join">
                      <div className="ad-break-share-qr-slot">
                        <div className="ad-break-share-qr">
                          {controllerQrImageUrl ? (
                            <img
                              src={controllerQrImageUrl}
                              alt="Scan to join the game now"
                            />
                          ) : (
                            <div className="qr-placeholder" aria-hidden="true" />
                          )}
                        </div>
                      </div>
                      <p
                        className={buildClassName(
                          "ad-break-share-caption",
                          gameplayTextFadeClass,
                        )}
                      >
                        Scan to join the game now.
                      </p>
                    </div>
                  )}
                  <div className="ad-break-timer-wrap">
                    <p
                      className={buildClassName(
                        "ad-break-timer-label",
                        gameplayTextFadeClass,
                      )}
                    >
                      {adBreakTimerLabel}
                    </p>
                    <div
                      className={buildClassName(
                        "ad-break-timer",
                        gameplayTextFadeClass,
                      )}
                    >
                      {adTimeLeft}
                    </div>
                  </div>
                </section>
              )}

            </LiveDisplayStageTransition>

            {!isAdBreak &&
              !isRoundIntro &&
              !isQuestionActive &&
              !isLeaderboardStage &&
              !funFact &&
              status === "Game Over" &&
              leaderboard.length === 0 && (
                <section className="card display-screen-card info-card">
                  <h2 className={gameplayTextFadeClass}>Thanks for playing!</h2>
                </section>
              )}
          </>
        )}
        </main>
      </DisplayErrorBoundary>
    </div>
  );
}

function App() {
  if (shouldRenderAnimationLab()) {
    return <DisplayAnimationLab />;
  }

  return <LiveDisplayApp />;
}

export default App;






